'use client';

import React, { useState, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

export interface PulseSliderProps {
  value?: number | null;
  onChange?: (value: number) => void;
  defaultValue?: number;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  className?: string;
  label?: string;
}

const PulseSlider: React.FC<PulseSliderProps> = ({
  value: controlledValue,
  onChange,
  defaultValue = 0,
  min = 0,
  max = 200,
  step = 1,
  disabled = false,
  className,
  label = 'Radius (miles)',
}): React.ReactElement => {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState<number>(defaultValue ?? 0);
  const currentValue = isControlled ? controlledValue ?? 0 : internalValue;
  const [inputValue, setInputValue] = useState<string>(String(currentValue));
  const sliderRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(String(currentValue));
  }, [currentValue]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = parseInt(e.target.value, 10);

    // Snap to every 5 after 10
    if (newValue > 10) {
      newValue = Math.round(newValue / 5) * 5;
    }

    if (isControlled) {
      onChange?.(newValue);
    } else {
      setInternalValue(newValue);
      onChange?.(newValue);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    const numValue = parseFloat(inputValue);
    if (isNaN(numValue)) {
      setInputValue(String(currentValue));
      return;
    }
    const clampedValue = Math.max(min, Math.min(max, numValue));

    // Snap to every 5 after 10, otherwise allow single units
    let snappedValue: number;
    if (clampedValue > 10) {
      snappedValue = Math.round(clampedValue / 5) * 5;
    } else {
      snappedValue = Math.round(clampedValue / step) * step;
    }

    setInputValue(String(snappedValue));

    if (snappedValue !== currentValue) {
      if (isControlled) {
        onChange?.(snappedValue);
      } else {
        setInternalValue(snappedValue);
        onChange?.(snappedValue);
      }
    }
  };

  const handleTickClick = (val: number) => {
    if (disabled) return;

    if (isControlled) {
      onChange?.(val);
    } else {
      setInternalValue(val);
      onChange?.(val);
    }
  };

  return (
    <div className={twMerge('w-full min-w-48 flex flex-col gap-2', className)}>
      <div className="flex items-center gap-3 ">
        <label className="text-primary text-sm font-semibold leading-tight">
          {label}
        </label>
        <input
          type="number"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          disabled={disabled}
          min={min}
          max={max}
          step={step}
          className={twMerge(
            'w-20 px-3 py-1.5 text-sm border border-neutral-300 rounded-full',
            'focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200',
            disabled && 'cursor-not-allowed opacity-50 bg-neutral-100'
          )}
        />
      </div>

      <div className="relative w-full">
        <div className="relative">
          <input
            ref={sliderRef}
            type="range"
            value={currentValue}
            onChange={handleSliderChange}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            aria-label={label}
            className={twMerge(
              'w-full h-2 rounded appearance-none cursor-pointer',
              'focus:outline-none',
              '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5',
              '[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:cursor-pointer',
              '[&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-sky-600 [&::-webkit-slider-thumb]:shadow-md',
              '[&::-webkit-slider-thumb]:hover:border-sky-700',
              '[&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full',
              '[&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-sky-600',
              '[&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:hover:border-sky-700',
              '[&::-moz-range-thumb]:border-none',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            style={{
              background: `linear-gradient(to right, #0284c7 ${
                ((currentValue - min) / (max - min)) * 100
              }%, #e2e8f0 ${((currentValue - min) / (max - min)) * 100}%)`,
            }}
          />
        </div>

        <div className="relative h-6">
          {/* Generate tick marks at every 5 (0, 5, 10, ..., 200) -
          full ticks with labels at every 10, half ticks at 5s */}
          {Array.from({ length: 41 }, (_, i) => i * 5).map((val) => {
            const percentage = ((val - min) / (max - min)) * 100;
            const thumbHalfWidth = 10;
            const offset = (50 - percentage) * (thumbHalfWidth / 50);
            const isFullTick = val % 10 === 0;

            // Responsive label visibility: show fewer labels on smaller screens
            // default (< md): show every 50 (0, 50, 100, 150, 200)
            // md: show every 25 (0, 25, 50, 75, 100, 125, 150, 175, 200)
            // lg+: show every 10 (0, 10, 20, ...)
            const showOnSmall = val % 50 === 0;
            const showOnMedium = val % 25 === 0;
            const showOnLarge = val % 10 === 0;

            let labelClasses =
              'text-[9px] text-neutral-500 mt-1 whitespace-nowrap hover:text-neutral-700 hover:font-semibold transition-all';

            // Build responsive visibility classes
            if (showOnSmall) {
              // Show on small, optionally hide on larger if not in those sets
              labelClasses += showOnMedium ? '' : ' md:hidden';
              labelClasses += showOnLarge ? '' : ' lg:hidden';
            } else if (showOnMedium) {
              // Hide on small, show on medium, optionally hide on large
              labelClasses += ' hidden md:inline';
              labelClasses += showOnLarge ? '' : ' lg:hidden';
            } else if (showOnLarge) {
              // Hide until large
              labelClasses += ' hidden lg:inline';
            } else {
              // Never show
              labelClasses += ' hidden';
            }

            return (
              <div
                key={val}
                className="absolute flex flex-col items-center cursor-pointer"
                style={{
                  left: `calc(${percentage}% + ${offset}px)`,
                  transform: 'translateX(-50%)',
                }}
                onClick={() => handleTickClick(val)}
              >
                <div
                  className={`w-px bg-neutral-400 ${
                    isFullTick ? 'h-2' : 'h-1'
                  }`}
                />
                {isFullTick && <span className={labelClasses}>{val}</span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PulseSlider;
